import { NavLink } from 'react-router-dom';
import { LayoutDashboard, MapPin, Settings, Bell, Key } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import AlertBadge from '../alerts/AlertBadge';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  labelKey: string;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t } = useTranslation();

  const navItems: NavItem[] = [
    { to: '/', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
    { to: '/sites', icon: MapPin, labelKey: 'nav.sites' },
    { to: '/alerts', icon: Bell, labelKey: 'nav.alerts' },
    { to: '/api-keys', icon: Key, labelKey: 'nav.apiKeys' },
    { to: '/settings', icon: Settings, labelKey: 'nav.settings' },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 lg:hidden z-40"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed lg:static inset-y-0 left-0 z-50 w-64 bg-gray-900 text-white transition-transform duration-300 ease-in-out',
          'flex flex-col',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => onClose()}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors',
                    'text-sm font-medium',
                    isActive
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  )
                }
                end={item.to === '/'}
              >
                <Icon className="w-5 h-5" />
                <span className="flex-1">{t(item.labelKey)}</span>
                {item.to === '/alerts' && <AlertBadge />}
              </NavLink>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
